import type { RouteRecordRaw } from 'vue-router';

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layout/AdminLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: {
          title: 'Home',
        },
      },
      {
        path: '/orders',
        name: 'orders',
        component: () => import('@/views/orders/OrderListView.vue'),
        meta: {
          title: 'Orders',
        },
      },
      {
        path: '/orders/:id',
        name: 'orderDetails',
        component: () => import('@/views/orders/OrderDetailsView.vue'),
        meta: {
          title: 'Orders Details',
        },
      },
      {
        path: '/users',
        name: 'users',
        component: () => import('@/views/users/UserListView.vue'),
        meta: {
          title: 'Users',
        },
      },
      {
        path: '/users/password-reset-requests',
        name: 'passwordResetRequests',
        component: () =>
          import('@/views/users/PasswordResetRequestsView.vue'),
        meta: {
          title: 'Password Reset Requests',
        },
      },
      {
        path: '/users/:id',
        name: 'usersDetails',
        component: () => import('@/views/users/UserView.vue'),
        meta: {
          title: 'Users Details',
        },
      },
      {
        path: '/supplier',
        name: 'supplier',
        component: () => import('@/views/supplier/SupplierListView.vue'),
        meta: {
          title: 'Supplier Management',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'DATA_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/supplier/create',
        name: 'createSupplierOrganization',
        component: () => import('@/views/supplier/CreateSupplierOrganizationView.vue'),
        meta: {
          title: 'Create Supplier Organization',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/supplier/organization/:id',
        name: 'supplierOrganizationDetails',
        component: () => import('@/views/supplier/SupplierOrganizationDetailView.vue'),
        meta: {
          title: 'Supplier Organization Details',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'DATA_ADMIN', 'ORDER_ADMIN', 'READ_ONLY'],
        },
        props: true,
      },
      {
        path: '/admin/lookups',
        name: 'Lookups',
        component: () => import('@/views/products/LookupView.vue'),
        meta: {
          title: 'Manage Lookups',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/admin/products',
        name: 'products',
        component: () => import('@/views/products/ProductManagementView.vue'),
        meta: {
          title: 'Manage Products',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'DATA_ADMIN', 'ORDER_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/admin/banners',
        name: 'banners',
        component: () => import('@/views/banner/BannerManagementView.vue'),
        meta: {
          title: 'Banners',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'DATA_ADMIN'],
        },
      },
      {
        path: '/admin/delivery',
        name: 'deliverySettings',
        component: () => import('@/views/delivery/DeliverySettingsView.vue'),
        meta: {
          title: 'Delivery & Promotions',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/trips',
        name: 'trips',
        component: () => import('@/views/trip/TripListView.vue'),
        meta: {
          title: 'Shipments',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'ORDER_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/trips/:id',
        name: 'tripDetails',
        component: () => import('@/views/trip/TripDetailView.vue'),
        meta: {
          title: 'Shipment Details',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'ORDER_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/sourcing',
        name: 'sourcing',
        component: () => import('@/views/sourcing/SourcingListView.vue'),
        meta: {
          title: 'Sourcing',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'ORDER_ADMIN', 'USER_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/sourcing/:id',
        name: 'sourcingBatch',
        component: () => import('@/views/sourcing/SourcingBatchDetailView.vue'),
        meta: {
          title: 'Sourcing Batch',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'ORDER_ADMIN', 'USER_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/retailer-confirmations',
        name: 'retailerConfirmations',
        component: () => import('@/views/sourcing/RetailerConfirmationListView.vue'),
        meta: {
          title: 'Retailer Confirmations',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'ORDER_ADMIN', 'USER_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: () => import('@/views/notification/NotificationListView.vue'),
        meta: {
          title: 'Notifications',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'ORDER_ADMIN'],
        },
      },
      {
        path: '/notifications/templates',
        name: 'notification-templates',
        component: () => import('@/views/notification/NotificationTemplatesView.vue'),
        meta: {
          title: 'Notification Templates',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
        },
      },
      {
        path: '/warehouses',
        name: 'warehouses',
        component: () => import('@/views/warehouse/WarehouseListView.vue'),
        meta: {
          title: 'Warehouses',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'DATA_ADMIN', 'READ_ONLY'],
        },
      },
      {
        path: '/warehouses/:id',
        name: 'warehouseDetails',
        component: () => import('@/views/warehouse/WarehouseDetailView.vue'),
        meta: {
          title: 'Warehouse Details',
          requiresAuth: true,
          roles: ['GLOBAL_ADMIN', 'DATA_ADMIN', 'READ_ONLY'],
        },
        props: true,
      },
      {
        path: '/supplier/product-catalog',
        name: 'supplierProductCatalog',
        component: () => import('@/views/catalog/ProductCatalogManagementView.vue'),
        meta: {
          title: 'Supplier Product Catalog',
          requiresAuth: true,
          roles: ['SUPPLIER_ADMIN'],
        },
      },
      {
        path: '/supplier/warehouses',
        name: 'supplierWarehouses',
        component: () => import('@/views/supplier/SupplierWarehouseListView.vue'),
        meta: {
          title: 'My Warehouses',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'CATALOG_SUPPLIER_ADMIN'],
        },
      },
      {
        path: '/supplier/warehouses/create',
        name: 'createSupplierWarehouse',
        component: () => import('@/views/supplier/CreateWarehouseView.vue'),
        meta: {
          title: 'Create Warehouse',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'CATALOG_SUPPLIER_ADMIN'],
        },
      },
      {
        path: '/supplier/warehouses/:id',
        name: 'supplierWarehouseDetails',
        component: () => import('@/views/supplier/SupplierWarehouseDetailView.vue'),
        meta: {
          title: 'Warehouse Details',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'CATALOG_SUPPLIER_ADMIN'],
        },
        props: true,
      },
      {
        path: '/supplier/warehouses/:id/catalog',
        name: 'supplierWarehouseCatalog',
        component: () => import('@/views/supplier/WarehouseCatalogView.vue'),
        meta: {
          title: 'Warehouse Catalog',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'CATALOG_SUPPLIER_ADMIN'],
        },
        props: true,
      },
      {
        path: '/supplier/sourcing',
        name: 'supplierSourcing',
        component: () => import('@/views/supplier/SupplierSourcingListView.vue'),
        meta: {
          title: 'Sourcing requests',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'ORDER_SUPPLIER_ADMIN'],
        },
      },
      {
        path: '/supplier/sourcing/:id',
        name: 'supplierSourcingDetail',
        component: () => import('@/views/supplier/SupplierSourcingBatchDetailView.vue'),
        meta: {
          title: 'Sourcing request',
          requiresAuth: true,
          roles: ['GLOBAL_SUPPLIER_ADMIN', 'ORDER_SUPPLIER_ADMIN'],
        },
      },
      // {
      //   path: '/supplier/:id',
      //   name: 'supplierDetails',
      //   component: () => import('@/views/supplier/SupplierDetailView.vue'),
      //   meta: {
      //     title: 'Supplier Details',
      //     requiresAuth: true,
      //     roles: ['GLOBAL_ADMIN', 'USER_ADMIN', 'DATA_ADMIN', 'ORDER_ADMIN', 'READ_ONLY'],
      //   },
      //   props: true,
      // },
      // {
      //   path: '/supplier/organizations/:id/edit',
      //   name: 'editSupplierOrganization',
      //   component: () => import('@/views/supplier/EditSupplierOrganizationView.vue'),
      //   meta: {
      //     title: 'Edit Supplier Organization',
      //     requiresAuth: true,
      //     roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
      //   },
      //   props: true,
      // },
      // {
      //   path: '/supplier/:id/edit',
      //   name: 'editSupplier',
      //   component: () => import('@/views/supplier/EditSupplierView.vue'),
      //   meta: {
      //     title: 'Edit Supplier',
      //     requiresAuth: true,
      //     roles: ['GLOBAL_ADMIN', 'USER_ADMIN'],
      //   },
      //   props: true,
      // },
    ],
  },
  {
    path: '/login',
    component: () => import('@/layout/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: {
          title: 'Login',
        },
      },
    ],
  },
  {
    path: '/register',
    component: () => import('@/layout/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: {
          title: 'Register',
        },
      },
    ],
  },
  {
    path: '/verify',
    component: () => import('@/layout/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'verify',
        component: () => import('@/views/auth/VerifyView.vue'),
        meta: {
          title: 'Verify',
        },
      },
    ],
  },
  {
    path: '/driver/:accessToken',
    name: 'driverTrip',
    component: () => import('@/views/trip/DriverTripView.vue'),
    meta: {
      title: 'Driver Trip View',
      public: true,
    },
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];
